import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  MoreHorizontal,
  Eye,
  Trash2,
  Check,
  X,
  Settings,
  LockOpen,
} from "lucide-react";
import { useTran } from "@/hooks";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { PATHS } from "@/lib/router";
import { useUserListDerived, useUserStore } from "../hooks";
import { normalizeId } from "../utils/roles-permissions";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";

const userLevelAdmin = ["super_admin", "reseller", "solution_provider"];

const UserActions = observer(
  ({ user, onViewUser, onDeleteUser, onUnlockUser }) => {
    const { t } = useTran();
    const { user: store_user, userRole } = useAuthStore();
    const navigate = useNavigate();
    const { isAdminRole, currentUserId } = useUserListDerived();
    const { followUpUserId, assignFollowUp, revokeFollowUp, loadUsers } =
      useUserStore();
    const normalizedCurrentUserId = normalizeId(currentUserId);
    const rowId = normalizeId(user?.id || user?._id);
    const userIsAdmin = userLevelAdmin.includes(userRole);
    const ownerIsAdmin = userLevelAdmin.includes(user?.role);
    const creatorId = normalizeId(user?.createdBy);
    const isMyUserRow =
      Boolean(normalizedCurrentUserId) &&
      ((rowId && rowId === normalizedCurrentUserId) ||
        (creatorId && creatorId === normalizedCurrentUserId));
    // const disabled = !isAdminRole && !isMyUserRow && !isSuperAdmin;
    const followUpLoading = followUpUserId === user?.id;

    const permissions = store_user?.permissions || [];

    // currently commeted out
    // if (disabled) {
    //   return (
    //     <Button variant="ghost" size="icon" aria-label={t("Actions")} disabled>
    //       <MoreHorizontal className="h-4 w-4" />
    //     </Button>
    //   );
    // }

    const hasIncharge = Boolean(user?.incharge?.name);
    const inchargeId = normalizeId(user?.incharge?.id);
    const isCurrentUserIncharge = inchargeId === normalizedCurrentUserId;

    // Role flags
    const isSystemAdmin = userRole === "system_admin";
    const isBusinessUnit = userRole === "business_unit";

    // Default permissions
    let canManage = false;
    let canDelete = false;
    let canFollowUp = false;
    let canUnfollowUp = false;

    if (user?.role === userRole && user?.id === currentUserId) {
      canManage = false;
      canDelete = false;
      canFollowUp = false;
      canUnfollowUp = false;
    }

    // ---- SYSTEM ADMIN ----
    else if (isSystemAdmin) {
      canManage = true;
      canDelete = true;

      if (hasIncharge) {
        canUnfollowUp = true;
      } else {
        canFollowUp = true;
      }
    }

    // ---- BUSINESS UNIT ----
    else if (isBusinessUnit) {
      // incharge exists and belongs to current business_unit
      if (hasIncharge && isCurrentUserIncharge) {
        canManage = true;
        canDelete = true;
        canUnfollowUp = true; // remove followup because incharge exists
      }
      // no incharge
      else if (!hasIncharge) {
        canFollowUp = true; // can assign follow-up
      }
      // incharge exists but is not this business_unit
      else {
        canManage = false;
        canDelete = false;
        canFollowUp = false;
        canUnfollowUp = false;
      }
    }

    // ---- USER LEVEL ADMIN ----
    else if (userIsAdmin) {
      canManage = true;
      canDelete = true;
      // no follow-up actions
    }

    const doAction = isAdminRole || userIsAdmin !== ownerIsAdmin; // actionable if admin or it's the user's own row
    const canUnlock = isAdminRole && user?.status === "locked";

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={t("Actions")}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel>{t("Actions")}</DropdownMenuLabel>

          <Can resource="user_list" action="view" userPermissions={permissions}>
            <DropdownMenuItem
              onSelect={() => onViewUser?.(user)}
              className="gap-2"
            >
              <Eye className="h-4 w-4 text-emerald-600" />
              {t("View")}
            </DropdownMenuItem>
          </Can>

          {canManage && (
            <>
              <Can
                resource="user_list"
                action="update"
                userPermissions={permissions}
                enabled={doAction}
              >
                <DropdownMenuItem
                  onSelect={() => {
                    const targetId = user?.id || user?.userId;
                    if (!targetId) return;
                    navigate(`${PATHS.MANAGE_USER}/${targetId}`);
                  }}
                  className="gap-2"
                >
                  <Settings className="h-4 w-4 text-blue-600" />
                  {t("Manage")}
                </DropdownMenuItem>
              </Can>
            </>
          )}

          {canUnlock && (
            <>
              <DropdownMenuSeparator />
              <Can
                resource="user_list"
                action="update"
                userPermissions={permissions}
                enabled={doAction}
              >
                <DropdownMenuItem
                  onSelect={() => onUnlockUser?.(user)}
                  className="gap-2"
                >
                  <LockOpen className="h-4 w-4 text-emerald-600" />
                  {t("Unlock")}
                </DropdownMenuItem>
              </Can>
            </>
          )}

          {canFollowUp && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={async () => {
                  if (followUpLoading) return;

                  const result = await assignFollowUp(user?.id);
                  if (result !== null) {
                    await loadUsers();
                  }
                }}
                disabled={followUpLoading}
                className="gap-2"
              >
                <Check className="h-4 w-4 text-blue-600" />
                {t("Follow Up")}
              </DropdownMenuItem>
            </>
          )}

          {canUnfollowUp && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={async () => {
                  if (followUpLoading) return;

                  const result = await revokeFollowUp(user?.id);
                  if (result !== null) {
                    await loadUsers();
                  }
                }}
                disabled={followUpLoading}
                className="gap-2"
              >
                <X className="h-4 w-4 text-red-600" />
                {t("Unfollow Up")}
              </DropdownMenuItem>
            </>
          )}

          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <Can
                resource="user_list"
                action="delete"
                userPermissions={permissions}
                enabled={doAction}
              >
                <DropdownMenuItem
                  onSelect={() => onDeleteUser?.(user)}
                  className="gap-2"
                  variant="destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  {t("Delete")}
                </DropdownMenuItem>
              </Can>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);

export default UserActions;
